/**
 * format.ts
 *
 * Shared formatting + sanitization utilities used across the app. This is
 * the single source of truth — older modules (format-inr.ts, strip-markdown.ts)
 * have been consolidated here.
 *
 * Rules of the road:
 *  - null / undefined / NaN → "—"   (never "null", "NaN", "₹0", or "0%")
 *  - explicit 0 → "0", "0%", or "₹0" as appropriate (genuine zero is meaningful)
 *  - All ₹ values render in the Indian number system (1,50,100 not 150,100)
 */

/* ------------------------------------------------------------------ */
/*  Number formatters                                                 */
/* ------------------------------------------------------------------ */

const INR_FORMATTER = new Intl.NumberFormat("en-IN", {
  maximumFractionDigits: 0,
});

/** ₹1,50,100 — rounded rupees, no paise. Null → "—". */
export function formatINR(value: number | null | undefined): string {
  if (value === null || value === undefined || !isFinite(value)) return "—";
  return `\u20B9${INR_FORMATTER.format(Math.round(value))}`;
}

/** Alias kept for backward compatibility with existing call sites. */
export const formatRupees = formatINR;

/** ₹1.5L / ₹5.2Cr / ₹50k — short axis labels. Null → "—". */
export function formatRupeesShort(value: number | null | undefined): string {
  if (value === null || value === undefined || !isFinite(value)) return "—";
  const abs = Math.abs(value);
  if (abs >= 1_00_00_000) return `\u20B9${(value / 1_00_00_000).toFixed(1)}Cr`;
  if (abs >= 1_00_000) return `\u20B9${(value / 1_00_000).toFixed(1)}L`;
  if (abs >= 1_000) return `\u20B9${(value / 1_000).toFixed(0)}k`;
  return `\u20B9${Math.round(value)}`;
}

/**
 * Fund AUM formatter. Input is in CRORES for INR funds (already converted
 * upstream), or raw base-currency units for non-INR. Null/0/negative → "—".
 */
export function formatAUM(
  crores: number | null | undefined,
  currency: string = "INR"
): string {
  if (crores === null || crores === undefined || !isFinite(crores) || crores <= 0) {
    return "—";
  }
  if (currency === "INR") {
    if (crores >= 100000) return `\u20B9${(crores / 100000).toFixed(2)} L Cr`;
    if (crores >= 1000) return `\u20B9${Math.round(crores).toLocaleString("en-IN")} Cr`;
    return `\u20B9${crores.toFixed(0)} Cr`;
  }
  if (crores >= 1_000_000_000) return `$${(crores / 1_000_000_000).toFixed(1)}B`;
  if (crores >= 1_000_000) return `$${(crores / 1_000_000).toFixed(0)}M`;
  return `$${crores.toLocaleString("en-US")}`;
}

/**
 * Signed percent from a decimal (0.125 → "+12.5%"). Null → "—", exact 0 →
 * "0.00%" when digits≥2 (genuine zero is explicit, never blank).
 */
export function formatPercent(
  decimal: number | null | undefined,
  digits = 1
): string {
  if (decimal === null || decimal === undefined || !isFinite(decimal)) return "—";
  const pct = decimal * 100;
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(digits)}%`;
}

/** Alias for spec naming. */
export const formatPctSigned = formatPercent;

/** Decimal (0.125) → "12.5%" (unsigned). Null → "—". */
export function formatPct(
  decimal: number | null | undefined,
  digits = 1
): string {
  if (decimal === null || decimal === undefined || !isFinite(decimal)) return "—";
  return `${(decimal * 100).toFixed(digits)}%`;
}

/** Already-percent number (e.g. -32.4) → "-32.4%". Null → "—". */
export function formatPctDirect(
  pct: number | null | undefined,
  digits = 1
): string {
  if (pct === null || pct === undefined || !isFinite(pct)) return "—";
  return `${pct.toFixed(digits)}%`;
}

/* ------------------------------------------------------------------ */
/*  Date formatters                                                   */
/* ------------------------------------------------------------------ */

/** ISO or "YYYY-MM-DD" → "Mar 25, 2026". Invalid/empty → "—". */
export function formatDate(raw: string | Date | null | undefined): string {
  if (!raw) return "—";
  const d = typeof raw === "string" ? new Date(raw) : raw;
  if (!(d instanceof Date) || isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/* ------------------------------------------------------------------ */
/*  Text sanitizers                                                   */
/* ------------------------------------------------------------------ */

/**
 * Remove common markdown formatting characters so a string renders cleanly
 * as plain text. Does NOT render markdown — just strips delimiters.
 */
export function stripMarkdown(text: string | null | undefined): string {
  if (!text) return "";
  return text
    .replace(/\*\*(.+?)\*\*/g, "$1") // **bold**
    .replace(/\*(.+?)\*/g, "$1") // *italic*
    .replace(/__(.+?)__/g, "$1") // __bold__
    .replace(/_(.+?)_/g, "$1") // _italic_
    .replace(/#{1,6}\s?/g, "") // # headings
    .replace(/`(.+?)`/g, "$1") // `code`
    .replace(/~~(.+?)~~/g, "$1") // ~~strike~~
    .replace(/\[(.+?)\]\(.+?\)/g, "$1") // [text](url)
    .replace(/^[-*+]\s+/gm, "") // bullet list prefixes
    .replace(/^\d+\.\s+/gm, "") // numbered list prefixes
    .replace(/^>\s?/gm, "") // > blockquotes
    .replace(/\n{2,}/g, " ") // collapse paragraph breaks
    .replace(/\n/g, " ") // remaining newlines
    .replace(/\s{2,}/g, " ") // collapse whitespace
    .trim();
}

/**
 * Strip HTML tags and decode common entities. `<br>` variants and `</p><p>`
 * become " | " separators. Used for fields like exit load that come from
 * upstream APIs with embedded HTML.
 */
export function stripHtml(raw: string | null | undefined): string {
  if (!raw) return "";
  return raw
    .replace(/<br\s*\/?>/gi, " | ")
    .replace(/<\/p>\s*<p[^>]*>/gi, " | ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s*\|\s*\|\s*/g, " | ")
    .replace(/\s+/g, " ")
    .replace(/^[\s|]+|[\s|]+$/g, "")
    .trim();
}
